import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UsersIcon,
  BellIcon,
  BellSlashIcon,
  FunnelIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  DocumentMagnifyingGlassIcon,
  CurrencyDollarIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const AdminLayout = ({ children, activeTab, onTabChange }) => {
  const navigate = useNavigate();

  const tabs = [
    {
      id: 'users',
      name: 'Users',
      icon: UsersIcon,
      path: '/admin/users',
      description: 'Manage user accounts and permissions'
    },
    {
      id: 'teams',
      name: 'Teams',
      icon: UserGroupIcon,
      path: '/admin/teams',
      description: 'Manage teams and collaboration'
    },
    {
      id: 'stakeholder-groups',
      name: 'Stakeholder Groups',
      icon: BuildingOfficeIcon,
      path: '/admin/stakeholder-groups',
      description: 'Manage data consumer groups'
    },
    {
      id: 'notifications',
      name: 'Notifications',
      icon: BellIcon,
      path: '/admin/notifications',
      description: 'Configure system notifications'
    },
    {
      id: 'snoozed-alerts',
      name: 'Snoozed Alerts',
      icon: BellSlashIcon,
      path: '/admin/snoozed-alerts',
      description: 'Manage temporarily silenced alerts'
    },
    {
      id: 'alert-routing',
      name: 'Alert Routing',
      icon: FunnelIcon,
      path: '/admin/alert-routing',
      description: 'Route alerts to different channels'
    },
    {
      id: 'organization',
      name: 'Organization',
      icon: Cog6ToothIcon,
      path: '/admin/organization',
      description: 'Organization settings and information'
    },
    {
      id: 'query-history',
      name: 'Query History',
      icon: DocumentMagnifyingGlassIcon,
      path: '/admin/query-history',
      description: 'View all database queries executed by Sparvi'
    },
    {
      id: 'cost-monitoring',
      name: 'Cost Monitoring',
      icon: CurrencyDollarIcon,
      path: '/admin/cost-monitoring',
      description: 'Monitor Snowflake credit consumption'
    },
    {
      id: 'query-performance',
      name: 'Query Performance',
      icon: ChartBarIcon,
      path: '/admin/query-performance',
      description: 'Analyze query performance and identify issues'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center mb-2">
          <ShieldCheckIcon className="h-6 w-6 text-primary-600 mr-2" />
          <h1 className="text-2xl font-semibold text-secondary-900">Administration</h1>
        </div>
        <p className="text-sm text-secondary-500">
          Manage organization settings, users, and system configuration.
        </p>
      </div>

      <div className="bg-white shadow rounded-lg">
        {/* Tabs */}
        <div className="border-b border-secondary-200">
          <nav className="-mb-px flex flex-wrap px-4 sm:px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    onTabChange(tab.id);
                    navigate(tab.path);
                  }}
                  className={`${
                    isActive
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                  } whitespace-nowrap py-4 px-2 mr-4 sm:mr-8 border-b-2 font-medium text-sm flex items-center group`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  <div className="text-left">
                    <div>{tab.name}</div>
                    {tab.description && (
                      <div className="text-xs text-secondary-400 font-normal">
                        {tab.description}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;